/**
 * FikoInteractiveDimension - Tam Özelleştirilebilir İnteraktif Ölçülendirme Sistemi
 *
 * Her segment ayrı ayrı özelleştirilebilir ve sürüklenebilir:
 * - Extension lines (uzatma çizgileri)
 * - Dimension line (ölçü çizgisi)
 * - Arrows (oklar)
 * - Label (değer etiketi)
 * - Leader lines (bağlantı çizgileri)
 *
 * Özellikler:
 * - Segment bazlı drag & drop
 * - Real-time değer güncelleme
 * - Snap-to-grid
 * - Undo/Redo desteği
 * - Preset stiller (CATIA, SolidWorks, Minimal, Technical)
 * - Animasyonlu geçişler
 *
 * @author FikoYT CAD Team
 * @version 2.0.0 - Full Interactive
 */

import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { getCanvasResolutionVec2 } from '../../../FikoCanvasResolution';

// ============================================================================
// ENUMS & TYPES
// ============================================================================

/**
 * Dimension segment tipleri
 */
export enum DimensionSegment {
    EXTENSION_LINE_1 = 'EXTENSION_LINE_1',
    EXTENSION_LINE_2 = 'EXTENSION_LINE_2',
    DIMENSION_LINE = 'DIMENSION_LINE',
    ARROW_1 = 'ARROW_1',
    ARROW_2 = 'ARROW_2',
    LABEL = 'LABEL',
    LEADER_LINE = 'LEADER_LINE',
    REFERENCE_POINT_1 = 'REFERENCE_POINT_1',
    REFERENCE_POINT_2 = 'REFERENCE_POINT_2'
}

/**
 * Ok tipleri
 */
export enum ArrowType {
    FILLED_TRIANGLE = 'FILLED_TRIANGLE',     // Dolu üçgen (default)
    OPEN_TRIANGLE = 'OPEN_TRIANGLE',         // Açık üçgen
    CLOSED_TRIANGLE = 'CLOSED_TRIANGLE',     // Kapalı üçgen
    DOT = 'DOT',                             // Nokta
    TICK = 'TICK',                           // Çizgi (mimari)
    OPEN_ARROW = 'OPEN_ARROW',               // Açık ok
    CLOSED_ARROW = 'CLOSED_ARROW',           // Kapalı ok
    NONE = 'NONE'                            // Ok yok
}

/**
 * Çizgi tipleri
 */
export enum LineType {
    SOLID = 'SOLID',
    DASHED = 'DASHED',
    DOTTED = 'DOTTED',
    DASH_DOT = 'DASH_DOT',
    CENTER = 'CENTER'
}

/**
 * Etiket pozisyonu
 */
export enum LabelPosition {
    ABOVE = 'ABOVE',                         // Çizginin üstünde
    BELOW = 'BELOW',                         // Çizginin altında
    CENTER = 'CENTER',                       // Merkez (çizgi kırılır)
    INLINE_LEFT = 'INLINE_LEFT',             // Sol hizada
    INLINE_RIGHT = 'INLINE_RIGHT',           // Sağ hizada
    CUSTOM = 'CUSTOM'                        // Özel pozisyon
}

/**
 * Preset stiller
 */
export enum DimensionPreset {
    CATIA = 'CATIA',                         // CATIA V5 stili (yeşil)
    SOLIDWORKS = 'SOLIDWORKS',               // SolidWorks stili (siyah)
    AUTOCAD = 'AUTOCAD',                     // AutoCAD stili
    MINIMAL = 'MINIMAL',                     // Minimal temiz stil
    TECHNICAL = 'TECHNICAL',                 // Teknik çizim stili
    BLUEPRINT = 'BLUEPRINT',                 // Mavi print stili
    CUSTOM = 'CUSTOM'                        // Özel stil
}

/**
 * Segment stil konfigürasyonu
 */
export interface SegmentStyle {
    visible: boolean;
    color: number;
    lineWidth: number;
    lineType: LineType;
    opacity: number;
}

/**
 * Ok stil konfigürasyonu
 */
export interface ArrowStyle {
    type: ArrowType;
    size: number;
    width: number;
    color: number;
    filled: boolean;
}

/**
 * Etiket stil konfigürasyonu
 */
export interface LabelStyle {
    visible: boolean;
    position: LabelPosition;
    offset: THREE.Vector3;
    fontSize: number;
    fontFamily: string;
    color: string;
    backgroundColor: string;
    backgroundOpacity: number;
    borderColor: string;
    borderWidth: number;
    padding: number;
    precision: number;
    unit: string;
    showUnit: boolean;
    prefix: string;
    suffix: string;
}

/**
 * Extension line stil konfigürasyonu
 */
export interface ExtensionLineStyle extends SegmentStyle {
    gap: number;                             // Başlangıç boşluğu
    overshoot: number;                       // Taşma miktarı
}

/**
 * Tam dimension konfigürasyonu
 */
export interface InteractiveDimensionConfig {
    // Segment stilleri
    extensionLine1: ExtensionLineStyle;
    extensionLine2: ExtensionLineStyle;
    dimensionLine: SegmentStyle;
    arrow1: ArrowStyle;
    arrow2: ArrowStyle;
    label: LabelStyle;

    // Genel ayarlar
    dimensionOffset: number;                 // Ölçü çizgisi offset'i
    snapToGrid: number | null;               // Grid snap değeri
    showReferencePoints: boolean;
    referencePointSize: number;
    referencePointColor: number;

    // İnteraktif ayarlar
    draggable: boolean;
    selectable: boolean;
    hoverable: boolean;
    editableValue: boolean;                  // Değer düzenlenebilir mi?

    // Animasyon
    animateChanges: boolean;
    animationDuration: number;
}

/**
 * Segment durumu (interaktif)
 */
export interface SegmentState {
    selected: boolean;
    hovered: boolean;
    dragging: boolean;
    customOffset: THREE.Vector3;
}

// ============================================================================
// PRESET CONFIGURATIONS
// ============================================================================

const PRESET_EXTENSION_LINE: ExtensionLineStyle = {
    visible: true,
    color: 0x000000,
    lineWidth: 1,
    lineType: LineType.SOLID,
    opacity: 1,
    gap: 1.5,
    overshoot: 2
};

const PRESET_DIMENSION_LINE: SegmentStyle = {
    visible: true,
    color: 0x000000,
    lineWidth: 1.5,
    lineType: LineType.SOLID,
    opacity: 1
};

const PRESET_ARROW: ArrowStyle = {
    type: ArrowType.FILLED_TRIANGLE,
    size: 3,
    width: 1.2,
    color: 0x000000,
    filled: true
};

const PRESET_LABEL: LabelStyle = {
    visible: true,
    position: LabelPosition.ABOVE,
    offset: new THREE.Vector3(0, 2, 0),
    fontSize: 12,
    fontFamily: 'Arial, sans-serif',
    color: '#000000',
    backgroundColor: '#ffffff',
    backgroundOpacity: 0.9,
    borderColor: '#000000',
    borderWidth: 0,
    padding: 4,
    precision: 2,
    unit: 'mm',
    showUnit: false,
    prefix: '',
    suffix: ''
};

/**
 * Preset stilleri
 */
export const DIMENSION_PRESETS: Record<DimensionPreset, Partial<InteractiveDimensionConfig>> = {
    [DimensionPreset.CATIA]: {
        extensionLine1: { ...PRESET_EXTENSION_LINE, color: 0x00cc00 },
        extensionLine2: { ...PRESET_EXTENSION_LINE, color: 0x00cc00 },
        dimensionLine: { ...PRESET_DIMENSION_LINE, color: 0x00cc00 },
        arrow1: { ...PRESET_ARROW, color: 0x00cc00 },
        arrow2: { ...PRESET_ARROW, color: 0x00cc00 },
        label: { ...PRESET_LABEL, color: '#00cc00', backgroundColor: '#1a1a2e', backgroundOpacity: 0.95 }
    },
    [DimensionPreset.SOLIDWORKS]: {
        extensionLine1: { ...PRESET_EXTENSION_LINE, color: 0x000000 },
        extensionLine2: { ...PRESET_EXTENSION_LINE, color: 0x000000 },
        dimensionLine: { ...PRESET_DIMENSION_LINE, color: 0x000000, lineWidth: 1.5 },
        arrow1: { ...PRESET_ARROW, color: 0x000000, size: 2.5 },
        arrow2: { ...PRESET_ARROW, color: 0x000000, size: 2.5 },
        label: { ...PRESET_LABEL, color: '#000000', backgroundColor: '#ffffff', borderWidth: 0 }
    },
    [DimensionPreset.AUTOCAD]: {
        extensionLine1: { ...PRESET_EXTENSION_LINE, color: 0xff0000 },
        extensionLine2: { ...PRESET_EXTENSION_LINE, color: 0xff0000 },
        dimensionLine: { ...PRESET_DIMENSION_LINE, color: 0xff0000 },
        arrow1: { ...PRESET_ARROW, color: 0xff0000, type: ArrowType.CLOSED_ARROW },
        arrow2: { ...PRESET_ARROW, color: 0xff0000, type: ArrowType.CLOSED_ARROW },
        label: { ...PRESET_LABEL, color: '#ff0000' }
    },
    [DimensionPreset.MINIMAL]: {
        extensionLine1: { ...PRESET_EXTENSION_LINE, color: 0x666666, lineWidth: 0.5 },
        extensionLine2: { ...PRESET_EXTENSION_LINE, color: 0x666666, lineWidth: 0.5 },
        dimensionLine: { ...PRESET_DIMENSION_LINE, color: 0x333333, lineWidth: 1 },
        arrow1: { ...PRESET_ARROW, color: 0x333333, size: 2, type: ArrowType.TICK },
        arrow2: { ...PRESET_ARROW, color: 0x333333, size: 2, type: ArrowType.TICK },
        label: { ...PRESET_LABEL, color: '#333333', fontSize: 10, backgroundOpacity: 0 }
    },
    [DimensionPreset.TECHNICAL]: {
        extensionLine1: { ...PRESET_EXTENSION_LINE, color: 0x000000, lineWidth: 0.5 },
        extensionLine2: { ...PRESET_EXTENSION_LINE, color: 0x000000, lineWidth: 0.5 },
        dimensionLine: { ...PRESET_DIMENSION_LINE, color: 0x000000, lineWidth: 0.7 },
        arrow1: { ...PRESET_ARROW, color: 0x000000, size: 2.5, type: ArrowType.FILLED_TRIANGLE },
        arrow2: { ...PRESET_ARROW, color: 0x000000, size: 2.5, type: ArrowType.FILLED_TRIANGLE },
        label: { ...PRESET_LABEL, color: '#000000', fontSize: 11, backgroundOpacity: 1 }
    },
    [DimensionPreset.BLUEPRINT]: {
        extensionLine1: { ...PRESET_EXTENSION_LINE, color: 0x4169e1 },
        extensionLine2: { ...PRESET_EXTENSION_LINE, color: 0x4169e1 },
        dimensionLine: { ...PRESET_DIMENSION_LINE, color: 0x4169e1 },
        arrow1: { ...PRESET_ARROW, color: 0x4169e1 },
        arrow2: { ...PRESET_ARROW, color: 0x4169e1 },
        label: { ...PRESET_LABEL, color: '#4169e1', backgroundColor: '#f0f8ff' }
    },
    [DimensionPreset.CUSTOM]: {}
};

/**
 * Varsayılan konfigürasyon
 */
export const DEFAULT_INTERACTIVE_CONFIG: InteractiveDimensionConfig = {
    extensionLine1: { ...PRESET_EXTENSION_LINE },
    extensionLine2: { ...PRESET_EXTENSION_LINE },
    dimensionLine: { ...PRESET_DIMENSION_LINE },
    arrow1: { ...PRESET_ARROW },
    arrow2: { ...PRESET_ARROW },
    label: { ...PRESET_LABEL },
    dimensionOffset: 10,
    snapToGrid: null,
    showReferencePoints: true,
    referencePointSize: 0.5,
    referencePointColor: 0x000000,
    draggable: true,
    selectable: true,
    hoverable: true,
    editableValue: false,
    animateChanges: true,
    animationDuration: 200
};

// ============================================================================
// MAIN CLASS
// ============================================================================

/**
 * FikoInteractiveDimension - Tam özelleştirilebilir interaktif ölçü
 */
export class FikoInteractiveDimension extends THREE.Group {
    readonly dimensionType = 'FikoInteractiveDimension';

    // Noktalar
    private startPoint: THREE.Vector3 = new THREE.Vector3();
    private endPoint: THREE.Vector3 = new THREE.Vector3();
    private offsetDirection: THREE.Vector3 = new THREE.Vector3(0, 1, 0);

    // Konfigürasyon
    private config: InteractiveDimensionConfig;
    private currentPreset: DimensionPreset = DimensionPreset.CUSTOM;

    // Segment objeleri
    private segments: Map<DimensionSegment, THREE.Object3D> = new Map();
    private segmentStates: Map<DimensionSegment, SegmentState> = new Map();

    // Değer
    private _value: number = 0;
    private _displayText: string = '';

    // İnteraktif durum
    private _selectedSegment: DimensionSegment | null = null;
    private _hoveredSegment: DimensionSegment | null = null;
    private _isDragging: boolean = false;
    private dragStartPosition: THREE.Vector3 = new THREE.Vector3();

    // History for undo/redo
    private history: InteractiveDimensionConfig[] = [];
    private historyIndex: number = -1;
    private maxHistorySize: number = 50;

    // Event callbacks
    public onSegmentSelect?: (segment: DimensionSegment | null) => void;
    public onSegmentHover?: (segment: DimensionSegment | null) => void;
    public onValueChange?: (value: number) => void;
    public onConfigChange?: (config: InteractiveDimensionConfig) => void;
    public onDragStart?: (segment: DimensionSegment) => void;
    public onDrag?: (segment: DimensionSegment, delta: THREE.Vector3) => void;
    public onDragEnd?: (segment: DimensionSegment) => void;

    // ========================================================================
    // CONSTRUCTOR
    // ========================================================================

    constructor(
        start: THREE.Vector3,
        end: THREE.Vector3,
        config: Partial<InteractiveDimensionConfig> = {},
        preset: DimensionPreset = DimensionPreset.SOLIDWORKS
    ) {
        super();

        this.startPoint.copy(start);
        this.endPoint.copy(end);

        // Preset uygula
        this.currentPreset = preset;
        const presetConfig = DIMENSION_PRESETS[preset] || {};

        // Konfigürasyonu birleştir
        this.config = this.mergeConfig(DEFAULT_INTERACTIVE_CONFIG, presetConfig, config);

        // Segment durumlarını başlat
        this.initializeSegmentStates();

        // Değeri hesapla
        this._value = this.calculateValue();
        this._displayText = this.formatValue();

        // Render ayarları
        this.renderOrder = 1000;
        this.frustumCulled = false;

        // Oluştur
        this.build();

        // İlk durumu history'ye ekle
        this.saveToHistory();
    }

    // ========================================================================
    // PUBLIC API - GETTERS
    // ========================================================================

    public get value(): number {
        return this._value;
    }

    public get displayText(): string {
        return this._displayText;
    }

    public get selectedSegment(): DimensionSegment | null {
        return this._selectedSegment;
    }

    public get hoveredSegment(): DimensionSegment | null {
        return this._hoveredSegment;
    }

    public get isDragging(): boolean {
        return this._isDragging;
    }

    public get preset(): DimensionPreset {
        return this.currentPreset;
    }

    // ========================================================================
    // PUBLIC API - CONFIGURATION
    // ========================================================================

    /**
     * Preset uygula
     */
    public applyPreset(preset: DimensionPreset): void {
        this.currentPreset = preset;
        const presetConfig = DIMENSION_PRESETS[preset] || {};
        this.config = this.mergeConfig(DEFAULT_INTERACTIVE_CONFIG, presetConfig);
        this.rebuild();
        this.saveToHistory();
    }

    /**
     * Tam konfigürasyonu güncelle
     */
    public updateConfig(config: Partial<InteractiveDimensionConfig>): void {
        this.config = this.mergeConfig(this.config, config);
        this.currentPreset = DimensionPreset.CUSTOM;
        this.rebuild();
        this.saveToHistory();

        if (this.onConfigChange) {
            this.onConfigChange(this.config);
        }
    }

    /**
     * Segment stilini güncelle
     */
    public updateSegmentStyle(segment: DimensionSegment, style: Partial<SegmentStyle | ArrowStyle | LabelStyle | ExtensionLineStyle>): void {
        switch (segment) {
            case DimensionSegment.EXTENSION_LINE_1:
                this.config.extensionLine1 = { ...this.config.extensionLine1, ...style as ExtensionLineStyle };
                break;
            case DimensionSegment.EXTENSION_LINE_2:
                this.config.extensionLine2 = { ...this.config.extensionLine2, ...style as ExtensionLineStyle };
                break;
            case DimensionSegment.DIMENSION_LINE:
                this.config.dimensionLine = { ...this.config.dimensionLine, ...style as SegmentStyle };
                break;
            case DimensionSegment.ARROW_1:
                this.config.arrow1 = { ...this.config.arrow1, ...style as ArrowStyle };
                break;
            case DimensionSegment.ARROW_2:
                this.config.arrow2 = { ...this.config.arrow2, ...style as ArrowStyle };
                break;
            case DimensionSegment.LABEL:
                this.config.label = { ...this.config.label, ...style as LabelStyle };
                break;
        }

        this.currentPreset = DimensionPreset.CUSTOM;
        this.rebuildSegment(segment);
        this.saveToHistory();
    }

    /**
     * Segment görünürlüğünü değiştir
     */
    public setSegmentVisible(segment: DimensionSegment, visible: boolean): void {
        this.updateSegmentStyle(segment, { visible });
    }

    /**
     * Segment rengini değiştir
     */
    public setSegmentColor(segment: DimensionSegment, color: number | string): void {
        const colorNum = typeof color === 'string' ? parseInt(color.replace('#', ''), 16) : color;

        if (segment === DimensionSegment.LABEL) {
            this.updateSegmentStyle(segment, { color: typeof color === 'string' ? color : `#${color.toString(16).padStart(6, '0')}` });
        } else {
            this.updateSegmentStyle(segment, { color: colorNum });
        }
    }

    /**
     * Tüm segmentlerin rengini değiştir
     */
    public setAllColors(color: number): void {
        const colorStr = `#${color.toString(16).padStart(6, '0')}`;

        this.config.extensionLine1.color = color;
        this.config.extensionLine2.color = color;
        this.config.dimensionLine.color = color;
        this.config.arrow1.color = color;
        this.config.arrow2.color = color;
        this.config.label.color = colorStr;
        this.config.referencePointColor = color;

        this.rebuild();
        this.saveToHistory();
    }

    /**
     * Ok tipini değiştir
     */
    public setArrowType(type: ArrowType, which: 'both' | 'start' | 'end' = 'both'): void {
        if (which === 'both' || which === 'start') {
            this.config.arrow1.type = type;
        }
        if (which === 'both' || which === 'end') {
            this.config.arrow2.type = type;
        }
        this.rebuild();
        this.saveToHistory();
    }

    /**
     * Ok boyutunu değiştir
     */
    public setArrowSize(size: number, which: 'both' | 'start' | 'end' = 'both'): void {
        if (which === 'both' || which === 'start') {
            this.config.arrow1.size = size;
        }
        if (which === 'both' || which === 'end') {
            this.config.arrow2.size = size;
        }
        this.rebuild();
        this.saveToHistory();
    }

    /**
     * Etiket pozisyonunu değiştir
     */
    public setLabelPosition(position: LabelPosition, customOffset?: THREE.Vector3): void {
        this.config.label.position = position;
        if (customOffset) {
            this.config.label.offset = customOffset;
        }
        this.rebuildSegment(DimensionSegment.LABEL);
        this.saveToHistory();
    }

    /**
     * Dimension offset'ini değiştir
     */
    public setDimensionOffset(offset: number): void {
        this.config.dimensionOffset = offset;
        this.rebuild();
        this.saveToHistory();
    }

    /**
     * Extension line ayarlarını değiştir
     */
    public setExtensionLineSettings(gap: number, overshoot: number): void {
        this.config.extensionLine1.gap = gap;
        this.config.extensionLine1.overshoot = overshoot;
        this.config.extensionLine2.gap = gap;
        this.config.extensionLine2.overshoot = overshoot;
        this.rebuild();
        this.saveToHistory();
    }

    /**
     * Çizgi kalınlığını değiştir
     */
    public setLineWidth(width: number, segments: DimensionSegment[] = []): void {
        if (segments.length === 0) {
            // Tümüne uygula
            this.config.extensionLine1.lineWidth = width * 0.7;
            this.config.extensionLine2.lineWidth = width * 0.7;
            this.config.dimensionLine.lineWidth = width;
        } else {
            segments.forEach(segment => {
                if (segment === DimensionSegment.EXTENSION_LINE_1) {
                    this.config.extensionLine1.lineWidth = width;
                } else if (segment === DimensionSegment.EXTENSION_LINE_2) {
                    this.config.extensionLine2.lineWidth = width;
                } else if (segment === DimensionSegment.DIMENSION_LINE) {
                    this.config.dimensionLine.lineWidth = width;
                }
            });
        }
        this.rebuild();
        this.saveToHistory();
    }

    /**
     * Font ayarlarını değiştir
     */
    public setFontSettings(fontSize?: number, fontFamily?: string, precision?: number): void {
        if (fontSize !== undefined) this.config.label.fontSize = fontSize;
        if (fontFamily !== undefined) this.config.label.fontFamily = fontFamily;
        if (precision !== undefined) this.config.label.precision = precision;

        this._displayText = this.formatValue();
        this.rebuildSegment(DimensionSegment.LABEL);
        this.saveToHistory();
    }

    /**
     * Birim ayarlarını değiştir
     */
    public setUnitSettings(unit: string, showUnit: boolean, prefix?: string, suffix?: string): void {
        this.config.label.unit = unit;
        this.config.label.showUnit = showUnit;
        if (prefix !== undefined) this.config.label.prefix = prefix;
        if (suffix !== undefined) this.config.label.suffix = suffix;

        this._displayText = this.formatValue();
        this.rebuildSegment(DimensionSegment.LABEL);
        this.saveToHistory();
    }

    /**
     * Mevcut konfigürasyonu al
     */
    public getConfig(): InteractiveDimensionConfig {
        return JSON.parse(JSON.stringify(this.config));
    }

    // ========================================================================
    // PUBLIC API - INTERACTION
    // ========================================================================

    /**
     * Segment seç
     */
    public selectSegment(segment: DimensionSegment | null): void {
        if (this._selectedSegment !== segment) {
            // Eski seçimi kaldır
            if (this._selectedSegment) {
                this.setSegmentState(this._selectedSegment, { selected: false });
            }

            this._selectedSegment = segment;

            // Yeni seçimi uygula
            if (segment) {
                this.setSegmentState(segment, { selected: true });
            }

            this.updateVisualStates();

            if (this.onSegmentSelect) {
                this.onSegmentSelect(segment);
            }
        }
    }

    /**
     * Segment hover
     */
    public hoverSegment(segment: DimensionSegment | null): void {
        if (!this.config.hoverable) return;

        if (this._hoveredSegment !== segment) {
            // Eski hover'ı kaldır
            if (this._hoveredSegment) {
                this.setSegmentState(this._hoveredSegment, { hovered: false });
            }

            this._hoveredSegment = segment;

            // Yeni hover'ı uygula
            if (segment) {
                this.setSegmentState(segment, { hovered: true });
            }

            this.updateVisualStates();

            if (this.onSegmentHover) {
                this.onSegmentHover(segment);
            }
        }
    }

    /**
     * Sürüklemeyi başlat
     */
    public startDrag(segment: DimensionSegment, startPosition: THREE.Vector3): void {
        if (!this.config.draggable) return;

        this._isDragging = true;
        this._selectedSegment = segment;
        this.dragStartPosition.copy(startPosition);
        this.setSegmentState(segment, { dragging: true });

        if (this.onDragStart) {
            this.onDragStart(segment);
        }
    }

    /**
     * Sürükle
     */
    public drag(currentPosition: THREE.Vector3): void {
        if (!this._isDragging || !this._selectedSegment) return;

        let delta = currentPosition.clone().sub(this.dragStartPosition);

        // Grid snap uygula
        if (this.config.snapToGrid) {
            const snap = this.config.snapToGrid;
            delta.x = Math.round(delta.x / snap) * snap;
            delta.y = Math.round(delta.y / snap) * snap;
            delta.z = Math.round(delta.z / snap) * snap;
        }

        // Segment'e göre offset güncelle
        this.applyDragToSegment(this._selectedSegment, delta);

        this.dragStartPosition.copy(currentPosition);

        if (this.onDrag) {
            this.onDrag(this._selectedSegment, delta);
        }
    }

    /**
     * Sürüklemeyi bitir
     */
    public endDrag(): void {
        if (!this._isDragging || !this._selectedSegment) return;

        this.setSegmentState(this._selectedSegment, { dragging: false });
        this._isDragging = false;

        this.saveToHistory();

        if (this.onDragEnd) {
            this.onDragEnd(this._selectedSegment);
        }
    }

    /**
     * Noktaları güncelle
     */
    public setPoints(start: THREE.Vector3, end: THREE.Vector3): void {
        this.startPoint.copy(start);
        this.endPoint.copy(end);
        this._value = this.calculateValue();
        this._displayText = this.formatValue();
        this.rebuild();

        if (this.onValueChange) {
            this.onValueChange(this._value);
        }
    }

    /**
     * Raycast için segment bul
     */
    public getSegmentAtPoint(raycaster: THREE.Raycaster): DimensionSegment | null {
        let closestSegment: DimensionSegment | null = null;
        let closestDistance = Infinity;

        this.segments.forEach((obj, segment) => {
            const intersects = raycaster.intersectObject(obj, true);
            if (intersects.length > 0 && intersects[0].distance < closestDistance) {
                closestDistance = intersects[0].distance;
                closestSegment = segment;
            }
        });

        return closestSegment;
    }

    // ========================================================================
    // PUBLIC API - HISTORY
    // ========================================================================

    /**
     * Geri al
     */
    public undo(): boolean {
        if (this.historyIndex > 0) {
            this.historyIndex--;
            this.config = JSON.parse(JSON.stringify(this.history[this.historyIndex]));
            this.rebuild();
            return true;
        }
        return false;
    }

    /**
     * İleri al
     */
    public redo(): boolean {
        if (this.historyIndex < this.history.length - 1) {
            this.historyIndex++;
            this.config = JSON.parse(JSON.stringify(this.history[this.historyIndex]));
            this.rebuild();
            return true;
        }
        return false;
    }

    /**
     * History'yi temizle
     */
    public clearHistory(): void {
        this.history = [JSON.parse(JSON.stringify(this.config))];
        this.historyIndex = 0;
    }

    // ========================================================================
    // PUBLIC API - LIFECYCLE
    // ========================================================================

    /**
     * Yeniden oluştur
     */
    public rebuild(): void {
        this.disposeSegments();
        this.build();
    }

    /**
     * Temizlik
     */
    public dispose(): void {
        this.disposeSegments();
        this.history = [];
    }

    // ========================================================================
    // PRIVATE - BUILD
    // ========================================================================

    private build(): void {
        const offset = this.config.dimensionOffset;
        const direction = this.calculateOffsetDirection();

        const dimStart = this.startPoint.clone().add(direction.clone().multiplyScalar(offset));
        const dimEnd = this.endPoint.clone().add(direction.clone().multiplyScalar(offset));

        // Extension lines
        if (this.config.extensionLine1.visible) {
            this.buildExtensionLine(this.startPoint, dimStart, DimensionSegment.EXTENSION_LINE_1, this.config.extensionLine1);
        }
        if (this.config.extensionLine2.visible) {
            this.buildExtensionLine(this.endPoint, dimEnd, DimensionSegment.EXTENSION_LINE_2, this.config.extensionLine2);
        }

        // Dimension line
        if (this.config.dimensionLine.visible) {
            this.buildDimensionLine(dimStart, dimEnd);
        }

        // Arrows
        this.buildArrow(dimStart, dimEnd, DimensionSegment.ARROW_1, this.config.arrow1, true);
        this.buildArrow(dimEnd, dimStart, DimensionSegment.ARROW_2, this.config.arrow2, false);

        // Label
        if (this.config.label.visible) {
            this.buildLabel(dimStart, dimEnd);
        }

        // Reference points
        if (this.config.showReferencePoints) {
            this.buildReferencePoint(this.startPoint, DimensionSegment.REFERENCE_POINT_1);
            this.buildReferencePoint(this.endPoint, DimensionSegment.REFERENCE_POINT_2);
        }
    }

    private buildExtensionLine(start: THREE.Vector3, end: THREE.Vector3, segment: DimensionSegment, style: ExtensionLineStyle): void {
        const direction = end.clone().sub(start).normalize();
        const gapStart = start.clone().add(direction.clone().multiplyScalar(style.gap));
        const overshootEnd = end.clone().add(direction.clone().multiplyScalar(style.overshoot));

        // Custom offset uygula
        const state = this.segmentStates.get(segment);
        if (state?.customOffset) {
            gapStart.add(state.customOffset);
            overshootEnd.add(state.customOffset);
        }

        const positions = [
            gapStart.x, gapStart.y, gapStart.z,
            overshootEnd.x, overshootEnd.y, overshootEnd.z
        ];

        const geometry = new LineGeometry();
        geometry.setPositions(positions);

        const material = new LineMaterial({
            color: style.color,
            linewidth: style.lineWidth * 0.001,
            transparent: style.opacity < 1,
            opacity: style.opacity,
            resolution: getCanvasResolutionVec2()
        });

        // Dashed style
        if (style.lineType === LineType.DASHED) {
            material.dashed = true;
            material.dashSize = 2;
            material.gapSize = 1;
        }

        const line = new Line2(geometry, material);
        line.computeLineDistances();
        line.userData = { segment, isInteractive: true };

        this.segments.set(segment, line);
        this.add(line);
    }

    private buildDimensionLine(start: THREE.Vector3, end: THREE.Vector3): void {
        const style = this.config.dimensionLine;

        const positions = [
            start.x, start.y, start.z,
            end.x, end.y, end.z
        ];

        const geometry = new LineGeometry();
        geometry.setPositions(positions);

        const material = new LineMaterial({
            color: style.color,
            linewidth: style.lineWidth * 0.001,
            transparent: style.opacity < 1,
            opacity: style.opacity,
            resolution: getCanvasResolutionVec2()
        });

        const line = new Line2(geometry, material);
        line.computeLineDistances();
        line.userData = { segment: DimensionSegment.DIMENSION_LINE, isInteractive: true };

        this.segments.set(DimensionSegment.DIMENSION_LINE, line);
        this.add(line);
    }

    private buildArrow(position: THREE.Vector3, target: THREE.Vector3, segment: DimensionSegment, style: ArrowStyle, isFirst: boolean): void {
        if (style.type === ArrowType.NONE) return;

        const direction = target.clone().sub(position).normalize();
        let geometry: THREE.BufferGeometry;

        switch (style.type) {
            case ArrowType.FILLED_TRIANGLE:
            case ArrowType.CLOSED_TRIANGLE:
                geometry = this.createFilledTriangleGeometry(style.size, style.width);
                break;
            case ArrowType.OPEN_TRIANGLE:
                geometry = this.createOpenTriangleGeometry(style.size, style.width);
                break;
            case ArrowType.DOT:
                geometry = new THREE.CircleGeometry(style.width * 0.5, 16);
                break;
            case ArrowType.TICK:
                geometry = this.createTickGeometry(style.size);
                break;
            case ArrowType.OPEN_ARROW:
                geometry = this.createOpenArrowGeometry(style.size, style.width);
                break;
            case ArrowType.CLOSED_ARROW:
                geometry = this.createClosedArrowGeometry(style.size, style.width);
                break;
            default:
                geometry = this.createFilledTriangleGeometry(style.size, style.width);
        }

        const material = new THREE.MeshBasicMaterial({
            color: style.color,
            side: THREE.DoubleSide
        });

        const arrow = new THREE.Mesh(geometry, material);
        arrow.position.copy(position);

        const angle = Math.atan2(direction.y, direction.x);
        arrow.rotation.z = angle;

        arrow.userData = { segment, isInteractive: true };

        this.segments.set(segment, arrow);
        this.add(arrow);
    }

    private buildLabel(dimStart: THREE.Vector3, dimEnd: THREE.Vector3): void {
        const style = this.config.label;

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        canvas.width = 256;
        canvas.height = 64;

        // Arka plan
        if (style.backgroundOpacity > 0) {
            ctx.fillStyle = style.backgroundColor;
            ctx.globalAlpha = style.backgroundOpacity;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.globalAlpha = 1;
        }

        // Border
        if (style.borderWidth > 0) {
            ctx.strokeStyle = style.borderColor;
            ctx.lineWidth = style.borderWidth * 2;
            ctx.strokeRect(0, 0, canvas.width, canvas.height);
        }

        // Metin
        ctx.font = `bold ${style.fontSize * 3}px ${style.fontFamily}`;
        ctx.fillStyle = style.color;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(this._displayText, canvas.width / 2, canvas.height / 2);

        const texture = new THREE.CanvasTexture(canvas);
        texture.needsUpdate = true;

        const spriteMaterial = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            depthTest: false
        });

        const sprite = new THREE.Sprite(spriteMaterial);

        // Pozisyon hesapla
        const midPoint = dimStart.clone().add(dimEnd).multiplyScalar(0.5);
        const offsetDir = this.calculateOffsetDirection();

        let labelOffset = new THREE.Vector3();
        switch (style.position) {
            case LabelPosition.ABOVE:
                labelOffset.copy(offsetDir).multiplyScalar(3);
                break;
            case LabelPosition.BELOW:
                labelOffset.copy(offsetDir).multiplyScalar(-3);
                break;
            case LabelPosition.CENTER:
                labelOffset.set(0, 0, 0);
                break;
            case LabelPosition.CUSTOM:
                labelOffset.copy(style.offset);
                break;
        }

        // Custom offset uygula
        const state = this.segmentStates.get(DimensionSegment.LABEL);
        if (state?.customOffset) {
            labelOffset.add(state.customOffset);
        }

        sprite.position.copy(midPoint).add(labelOffset);

        const scale = 12;
        sprite.scale.set(scale, scale * 0.25, 1);

        sprite.userData = { segment: DimensionSegment.LABEL, isInteractive: true };

        this.segments.set(DimensionSegment.LABEL, sprite);
        this.add(sprite);
    }

    private buildReferencePoint(position: THREE.Vector3, segment: DimensionSegment): void {
        const geometry = new THREE.CircleGeometry(this.config.referencePointSize, 8);
        const material = new THREE.MeshBasicMaterial({
            color: this.config.referencePointColor,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: 0.5
        });

        const marker = new THREE.Mesh(geometry, material);
        marker.position.copy(position);
        marker.userData = { segment, isInteractive: false };

        this.segments.set(segment, marker);
        this.add(marker);
    }

    // ========================================================================
    // PRIVATE - GEOMETRY HELPERS
    // ========================================================================

    private createFilledTriangleGeometry(length: number, width: number): THREE.BufferGeometry {
        const shape = new THREE.Shape();
        shape.moveTo(0, 0);
        shape.lineTo(-length, width / 2);
        shape.lineTo(-length * 0.7, 0);
        shape.lineTo(-length, -width / 2);
        shape.closePath();
        return new THREE.ShapeGeometry(shape);
    }

    private createOpenTriangleGeometry(length: number, width: number): THREE.BufferGeometry {
        const points = [
            new THREE.Vector3(-length, width / 2, 0),
            new THREE.Vector3(0, 0, 0),
            new THREE.Vector3(-length, -width / 2, 0)
        ];
        return new THREE.BufferGeometry().setFromPoints(points);
    }

    private createTickGeometry(length: number): THREE.BufferGeometry {
        const points = [
            new THREE.Vector3(-length / 2, length / 2, 0),
            new THREE.Vector3(length / 2, -length / 2, 0)
        ];
        return new THREE.BufferGeometry().setFromPoints(points);
    }

    private createOpenArrowGeometry(length: number, width: number): THREE.BufferGeometry {
        const points = [
            new THREE.Vector3(-length, width / 2, 0),
            new THREE.Vector3(0, 0, 0),
            new THREE.Vector3(-length, -width / 2, 0)
        ];
        return new THREE.BufferGeometry().setFromPoints(points);
    }

    private createClosedArrowGeometry(length: number, width: number): THREE.BufferGeometry {
        const shape = new THREE.Shape();
        shape.moveTo(0, 0);
        shape.lineTo(-length, width / 2);
        shape.lineTo(-length, -width / 2);
        shape.closePath();
        return new THREE.ShapeGeometry(shape);
    }

    // ========================================================================
    // PRIVATE - UTILITIES
    // ========================================================================

    private calculateOffsetDirection(): THREE.Vector3 {
        const lineDir = this.endPoint.clone().sub(this.startPoint).normalize();
        const up = new THREE.Vector3(0, 0, 1);
        const perp = new THREE.Vector3().crossVectors(lineDir, up);

        if (perp.lengthSq() < 0.001) {
            perp.crossVectors(lineDir, new THREE.Vector3(0, 1, 0));
        }

        return perp.normalize();
    }

    private calculateValue(): number {
        return this.startPoint.distanceTo(this.endPoint);
    }

    private formatValue(): string {
        const style = this.config.label;
        let text = style.prefix;
        text += this._value.toFixed(style.precision);
        if (style.showUnit) {
            text += ' ' + style.unit;
        }
        text += style.suffix;
        return text;
    }

    private mergeConfig(...configs: Partial<InteractiveDimensionConfig>[]): InteractiveDimensionConfig {
        let result = JSON.parse(JSON.stringify(DEFAULT_INTERACTIVE_CONFIG));

        configs.forEach(config => {
            if (config) {
                Object.keys(config).forEach(key => {
                    const value = (config as any)[key];
                    if (value !== undefined && value !== null) {
                        if (typeof value === 'object' && !Array.isArray(value) && !(value instanceof THREE.Vector3)) {
                            (result as any)[key] = { ...(result as any)[key], ...value };
                        } else {
                            (result as any)[key] = value;
                        }
                    }
                });
            }
        });

        return result;
    }

    private initializeSegmentStates(): void {
        Object.values(DimensionSegment).forEach(segment => {
            this.segmentStates.set(segment, {
                selected: false,
                hovered: false,
                dragging: false,
                customOffset: new THREE.Vector3()
            });
        });
    }

    private setSegmentState(segment: DimensionSegment, state: Partial<SegmentState>): void {
        const current = this.segmentStates.get(segment);
        if (current) {
            this.segmentStates.set(segment, { ...current, ...state });
        }
    }

    private applyDragToSegment(segment: DimensionSegment, delta: THREE.Vector3): void {
        const state = this.segmentStates.get(segment);
        if (state) {
            state.customOffset.add(delta);

            // Dimension offset için özel işlem
            if (segment === DimensionSegment.DIMENSION_LINE) {
                const offsetDir = this.calculateOffsetDirection();
                const projection = delta.dot(offsetDir);
                this.config.dimensionOffset += projection;
            }

            this.rebuildSegment(segment);
        }
    }

    private rebuildSegment(segment: DimensionSegment): void {
        // Sadece o segmenti yeniden oluştur
        const obj = this.segments.get(segment);
        if (obj) {
            this.disposeObject(obj);
            this.remove(obj);
            this.segments.delete(segment);
        }

        // Segment'i yeniden oluştur
        const offset = this.config.dimensionOffset;
        const direction = this.calculateOffsetDirection();
        const dimStart = this.startPoint.clone().add(direction.clone().multiplyScalar(offset));
        const dimEnd = this.endPoint.clone().add(direction.clone().multiplyScalar(offset));

        switch (segment) {
            case DimensionSegment.EXTENSION_LINE_1:
                if (this.config.extensionLine1.visible) {
                    this.buildExtensionLine(this.startPoint, dimStart, segment, this.config.extensionLine1);
                }
                break;
            case DimensionSegment.EXTENSION_LINE_2:
                if (this.config.extensionLine2.visible) {
                    this.buildExtensionLine(this.endPoint, dimEnd, segment, this.config.extensionLine2);
                }
                break;
            case DimensionSegment.DIMENSION_LINE:
                if (this.config.dimensionLine.visible) {
                    this.buildDimensionLine(dimStart, dimEnd);
                }
                break;
            case DimensionSegment.ARROW_1:
                this.buildArrow(dimStart, dimEnd, segment, this.config.arrow1, true);
                break;
            case DimensionSegment.ARROW_2:
                this.buildArrow(dimEnd, dimStart, segment, this.config.arrow2, false);
                break;
            case DimensionSegment.LABEL:
                if (this.config.label.visible) {
                    this.buildLabel(dimStart, dimEnd);
                }
                break;
        }
    }

    private updateVisualStates(): void {
        this.segments.forEach((obj, segment) => {
            const state = this.segmentStates.get(segment);
            if (!state) return;

            // Seçili veya hover durumuna göre görsel güncelle
            if (obj instanceof THREE.Mesh) {
                const material = obj.material as THREE.MeshBasicMaterial;
                if (state.selected) {
                    material.color.setHex(0xffff00); // Sarı
                } else if (state.hovered) {
                    material.color.setHex(0x00ffff); // Cyan
                } else {
                    // Orijinal renge dön
                    const config = this.getSegmentConfig(segment);
                    if (config && 'color' in config) {
                        material.color.setHex(config.color as number);
                    }
                }
            } else if (obj instanceof Line2) {
                const material = obj.material as LineMaterial;
                if (state.selected) {
                    material.color.setHex(0xffff00);
                } else if (state.hovered) {
                    material.color.setHex(0x00ffff);
                } else {
                    const config = this.getSegmentConfig(segment);
                    if (config && 'color' in config) {
                        material.color.setHex(config.color as number);
                    }
                }
            }
        });
    }

    private getSegmentConfig(segment: DimensionSegment): SegmentStyle | ArrowStyle | LabelStyle | ExtensionLineStyle | null {
        switch (segment) {
            case DimensionSegment.EXTENSION_LINE_1:
                return this.config.extensionLine1;
            case DimensionSegment.EXTENSION_LINE_2:
                return this.config.extensionLine2;
            case DimensionSegment.DIMENSION_LINE:
                return this.config.dimensionLine;
            case DimensionSegment.ARROW_1:
                return this.config.arrow1;
            case DimensionSegment.ARROW_2:
                return this.config.arrow2;
            case DimensionSegment.LABEL:
                return this.config.label;
            default:
                return null;
        }
    }

    private saveToHistory(): void {
        // Mevcut index'ten sonrakileri sil
        this.history = this.history.slice(0, this.historyIndex + 1);

        // Yeni durumu ekle
        this.history.push(JSON.parse(JSON.stringify(this.config)));
        this.historyIndex = this.history.length - 1;

        // Maksimum boyutu aşarsa eskilerini sil
        if (this.history.length > this.maxHistorySize) {
            this.history = this.history.slice(-this.maxHistorySize);
            this.historyIndex = this.history.length - 1;
        }
    }

    private disposeSegments(): void {
        this.segments.forEach((obj, segment) => {
            this.disposeObject(obj);
            this.remove(obj);
        });
        this.segments.clear();
    }

    private disposeObject(obj: THREE.Object3D): void {
        if (obj instanceof THREE.Mesh) {
            obj.geometry.dispose();
            if (obj.material instanceof THREE.Material) {
                obj.material.dispose();
            }
        } else if (obj instanceof Line2) {
            obj.geometry.dispose();
            (obj.material as THREE.Material).dispose();
        } else if (obj instanceof THREE.Sprite) {
            const mat = obj.material as THREE.SpriteMaterial;
            if (mat.map) mat.map.dispose();
            mat.dispose();
        }
    }
}

// ============================================================================
// FACTORY FUNCTIONS
// ============================================================================

/**
 * CATIA stili ölçü oluştur
 */
export function createCatiaDimension(
    start: THREE.Vector3,
    end: THREE.Vector3,
    config: Partial<InteractiveDimensionConfig> = {}
): FikoInteractiveDimension {
    return new FikoInteractiveDimension(start, end, config, DimensionPreset.CATIA);
}

/**
 * SolidWorks stili ölçü oluştur
 */
export function createSolidWorksDimension(
    start: THREE.Vector3,
    end: THREE.Vector3,
    config: Partial<InteractiveDimensionConfig> = {}
): FikoInteractiveDimension {
    return new FikoInteractiveDimension(start, end, config, DimensionPreset.SOLIDWORKS);
}

/**
 * Minimal stili ölçü oluştur
 */
export function createMinimalDimension(
    start: THREE.Vector3,
    end: THREE.Vector3,
    config: Partial<InteractiveDimensionConfig> = {}
): FikoInteractiveDimension {
    return new FikoInteractiveDimension(start, end, config, DimensionPreset.MINIMAL);
}

/**
 * Teknik çizim stili ölçü oluştur
 */
export function createTechnicalDimension(
    start: THREE.Vector3,
    end: THREE.Vector3,
    config: Partial<InteractiveDimensionConfig> = {}
): FikoInteractiveDimension {
    return new FikoInteractiveDimension(start, end, config, DimensionPreset.TECHNICAL);
}

// All exports are inline with their declarations above
